import type { ApiFileUploadType } from 'api/types/ApiFileUploadType';
import type { ApiS3FileId } from 'api/types/ApiTypedId';
import { Field } from 'components/Form/Field/Field';
import { useFormContext } from 'components/Form/FormContext';
import { SmallFilePicker } from 'components/SmallFilePicker/SmallFilePicker';
import { useS3FileUpload } from 'hooks/useS3FileUpload';

type Props = {
  analyticsLabel?: string;
  downloadAttachment: boolean;
  fileType?: ApiFileUploadType;
  name: string;
  label?: string;
  description?: string;
  qaId?: string;
  listingType?: string;
  disabled?: boolean;
};

export function RequestedAttachmentUserField({
  analyticsLabel,
  downloadAttachment,
  fileType = 'LISTING_APPLICATION',
  name,
  label,
  qaId,
  listingType,
  disabled: disabledProp,
  description,
}: Props) {
  const subName = `${name}.s3FileId`;

  const { getValue, updateValue, handleError } = useFormContext();

  const fileId = (getValue(subName) as ApiS3FileId | null | undefined) ?? null;
  const disabled = Boolean(disabledProp);
  const maxSizeInMB = 10;

  const { fileUploadStateAndActions } = useS3FileUpload({
    fileId,
    fileType,
    onChange: (newFileId) => {
      updateValue(
        name,
        newFileId ? { type: 'FILE', s3FileId: newFileId } : null,
      );
    },
    onError: handleError,
    canRemove: true,
    maxSizeInMB,
    extraAnalyticsData: {
      context: 'Listing Application Form',
      listing_type: listingType,
      file_label: analyticsLabel ?? label,
    },
  });

  return (
    <Field
      customErrorMatch={(errors) => errors[name] ?? errors[subName]}
      name={name}
      label={label}
      description={description}
      inputProps={{
        loading: fileUploadStateAndActions.loading,
        fileMeta: fileUploadStateAndActions.fileMeta,
      }}
      renderInput={({ qaId: fieldQaId }) => (
        <SmallFilePicker
          disabled={disabled}
          downloadAttachment={downloadAttachment}
          qaId={qaId ?? fieldQaId}
          name={name}
          accept="application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, image/jpeg, image/png, text/plain"
          maxSizeInMB={maxSizeInMB}
          required={false}
          ariaLabel={null}
          customMessage={null}
          showDate={false}
          fileUploadStateAndActions={fileUploadStateAndActions}
        />
      )}
    />
  );
}
